import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'flowbite-react';
import { Button } from '../button/button';
import { Text } from '../text/text';
import { type ComponentSize } from '../select/__commons__/selectCommons';
import { drawerModalTheme, modalTheme } from '../../styles/themes/modal';
import { ButtonColorsEnum } from '../../styles/themes/button';
import { DismissLayerContext, useDismissLayer } from '../../hooks/use-dismiss-layer';
import { useModalFocus } from '../../hooks/use-modal-focus';
import { InsideDrawerContext } from '../drawer/drawer-context';
// the app-import-free slice of `src/i18n` (see i18n-context.tsx)
import { useTranslations } from 'i18n/use-translations';

export type OverXlSize = '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';
export type EveryComponentSize = ComponentSize | OverXlSize;
export type ModalVariant = 'default' | 'destruct' | 'bottomDrawer';
export type ModalPlacement =
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'center-left'
    | 'center'
    | 'center-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';

export type ExtraButton = {
    label: string;
    action: () => void;
    color?: ButtonColorsEnum;
    startIcon?: React.ReactNode;
    disabled?: boolean;
};

export type ModalProps = {
    className?: string;
    id?: string;
    testId?: string;
    /** Fallback accessible name — only needed for headerless modals (aria-labelledby wins) */
    ariaLabel?: string;
    /** Id of the element that describes the dialog, e.g. a confirmation's message. */
    ariaDescribedBy?: string;
    size?: EveryComponentSize;
    rounding?: 'md' | 'lg' | '2xl';
    position?: ModalPlacement;
    variant?: ModalVariant;
    openModal: boolean;
    showHeader?: boolean;
    showFooter?: boolean;
    showCloseButton?: boolean;
    bodyClassName?: string;
    footerClassName?: string;
    dismissible?: boolean;
    handleClose: () => void;
    handleCancel: () => void;
    handleConfirm: () => void;
    showCancelButton?: boolean;
    showConfirmButton?: boolean;
    cancelDisabled?: boolean;
    confirmDisabled?: boolean;
    /** Close on Escape even when not `dismissible`. Defaults to true (WAI-ARIA dialog pattern) */
    closeOnEscape?: boolean;
    title?: string;
    subTitle?: string;
    subTitleClassName?: string;
    customCancelLabel?: string;
    customConfirmLabel?: string;
    extraButton?: ExtraButton;
    bodyHeight?: number;
    /** Where focus goes on close when the element that opened the modal is gone, e.g. a row it deleted. */
    onOpenerRemoved?: () => void;
    /** Stacks the modal above other open overlays, e.g. a legacy MUI dialog it was opened from. */
    aboveOverlays?: boolean;
    children: React.ReactNode;
};

export const ModalComponent: React.FC<ModalProps> = (props: ModalProps) => {
    const {
        className,
        id,
        testId,
        ariaLabel,
        ariaDescribedBy,
        size = 'md',
        rounding = 'md',
        position = 'center',
        variant = 'default',
        openModal,
        showHeader = true,
        showFooter = true,
        showCloseButton = true,
        bodyClassName,
        footerClassName,
        dismissible = true,
        handleClose,
        handleCancel,
        handleConfirm,
        showCancelButton = true,
        showConfirmButton = true,
        cancelDisabled = false,
        confirmDisabled = false,
        closeOnEscape = true,
        customCancelLabel,
        customConfirmLabel,
        extraButton,
        title,
        subTitle,
        subTitleClassName,
        bodyHeight,
        onOpenerRemoved,
        aboveOverlays = false,
        children,
    } = props;
    const destructMode = variant === 'destruct';
    const { __ } = useTranslations();
    const closeLabel = __('misc.close');

    const scrollRef = useRef<HTMLDivElement>(null);
    const [hasVerticalScroll, setHasVerticalScroll] = useState(false);

    // flowbite-react 0.7.5 spreads rest props onto the overlay as well as the dialog (an axe
    // violation), so dialog-only attributes are written to the dialog node, kept from the forwarded ref.
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const applyDialogAttributes = useCallback(() => {
        const dialog = dialogRef.current;
        if (!dialog) {
            return;
        }
        dialog.setAttribute('aria-modal', 'true');
        if (ariaLabel) {
            dialog.setAttribute('aria-label', ariaLabel);
        } else {
            // the same dialog node is reused across renders, so clear any stale name
            dialog.removeAttribute('aria-label');
        }
        if (ariaDescribedBy) {
            dialog.setAttribute('aria-describedby', ariaDescribedBy);
        } else {
            dialog.removeAttribute('aria-describedby');
        }
        // flowbite hardcodes the header close button's name as English "Close"
        const closeButton = dialog.querySelector('[data-modal-close], button[aria-label="Close"]');
        closeButton?.setAttribute('data-modal-close', '');
        closeButton?.setAttribute('aria-label', closeLabel);
    }, [ariaLabel, ariaDescribedBy, closeLabel]);

    // A callback ref, since the dialog portal-mounts after the first effect pass.
    const setDialog = useCallback(
        (dialog: HTMLDivElement | null) => {
            dialogRef.current = dialog;
            applyDialogAttributes();
        },
        [applyDialogAttributes],
    );

    // A header that mounts after the dialog (showHeader turning on) needs its close button renamed too.
    useLayoutEffect(applyDialogAttributes, [applyDialogAttributes, showHeader]);

    // flowbite only wires Escape when `dismissible`; the layer covers the rest, from anywhere while open.
    const dismissLayer = useDismissLayer({
        enabled: openModal && closeOnEscape,
        onDismiss: handleClose,
        getElements: () => [dialogRef.current, dialogRef.current?.parentElement],
        kind: 'container',
        modal: true,
    });

    useModalFocus({ open: openModal, dialogRef, onOpenerRemoved });

    useEffect(() => {
        if (!openModal) {
            return;
        }

        const timer = setTimeout(() => {
            const el = scrollRef.current;
            if (el && bodyHeight) {
                setHasVerticalScroll(el.scrollHeight > bodyHeight);
            }
        }, 0); // runs after next paint

        return () => clearTimeout(timer);
    }, [bodyHeight, openModal]);

    // Portalled to the body, so without this a modal opened from a Drawer would open underneath it
    const insideDrawer = useContext(InsideDrawerContext);
    const customTheme =
        variant === 'bottomDrawer'
            ? drawerModalTheme(openModal, showCloseButton)
            : modalTheme(`rounded-${rounding}`, showCloseButton);
    return (
        /* Modal includes the screen-filling layer outside the dialog border */
        <Modal
            theme={customTheme} // bottomDrawer variant has own theme
            className={insideDrawer || aboveOverlays ? `z-[9998] ${className ?? ''}` : className}
            show={openModal}
            size={destructMode ? 'md' : size}
            position={position}
            onClose={handleClose}
            id={id}
            data-testid={testId}
            dismissible={dismissible}
            ref={setDialog}
        >
            {showHeader && (
                /* ModalHeader's children render inside the <h3> flowbite wires to aria-labelledby,
                   so only phrasing content is valid here — spans, not <p> */
                <ModalHeader data-testid={'modal-header'} className="items-start">
                    <Text as="span" variant="title-large">
                        {title}
                    </Text>
                    {subTitle && (
                        <Text as="span" variant="body-small" className={`block pt-1 ${subTitleClassName}`}>
                            {subTitle}
                        </Text>
                    )}
                </ModalHeader>
            )}
            <div ref={scrollRef}>
                {/* 280/744 (from design) - 160 (for header + footer) */}
                <ModalBody
                    data-testid={'modal-body'}
                    style={{
                        minHeight: '120px',
                        maxHeight: '584px',
                        ...(!!bodyHeight ? { height: `${bodyHeight}px` } : {}),
                    }}
                    className={`${bodyClassName} space-y-4 ${
                        hasVerticalScroll ? 'border-y border-system-outline-variant rounded-none' : ''
                    }`}
                >
                    {/* content will scroll if overflowing the (optional) set bodyHeight */}
                    <DismissLayerContext.Provider value={dismissLayer}>{children}</DismissLayerContext.Provider>
                </ModalBody>
            </div>
            {showFooter && (
                <ModalFooter data-testid={'modal-footer'} className={footerClassName}>
                    {/* buttons row: each button optional */}
                    <div className="w-full flex flex-row justify-between gap-4">
                        {extraButton && (
                            <Button
                                data-testid={'modal-extra-button'}
                                onClick={extraButton.action}
                                label={extraButton.label}
                                size="sm"
                                startIcon={extraButton.startIcon ?? undefined}
                                color={extraButton.color ?? 'ghost'}
                                className={'px-3'}
                                disabled={!!extraButton.disabled}
                            />
                        )}
                        <div className="flex flex-row flex-grow justify-end gap-4">
                            {showCancelButton && (
                                <Button
                                    data-testid={'modal-cancel-button'}
                                    className=""
                                    onClick={handleCancel}
                                    label={customCancelLabel ?? __('misc.cancel')}
                                    size="sm"
                                    color="outline"
                                    disabled={cancelDisabled}
                                />
                            )}
                            {showConfirmButton && (
                                <Button
                                    data-testid={'modal-confirm-button'}
                                    onClick={handleConfirm}
                                    label={
                                        customConfirmLabel ?? (destructMode ? __('misc.destruct') : __('misc.confirm'))
                                    }
                                    size="sm"
                                    color={destructMode ? 'destruct' : 'primary'}
                                    disabled={confirmDisabled}
                                />
                            )}
                        </div>
                    </div>
                </ModalFooter>
            )}
        </Modal>
    );
};
